import { MidPoint, Point, PointType } from "../types";
import { bezierPathsForTouchPoints, touchableAnchorPointPathsForBezierPaths } from "./bezier-points";

export function distanceBetweenPoints(x1: number, y1: number, x2: number, y2: number): number {
  return Math.sqrt(Math.pow(x2 - x1, 2) + Math.pow(y2 - y1, 2));
}

export interface CalculatePathFromFreeDrawPointsProps {
  tracePathPointsArr: MidPoint[];
}

export const calculatePathFromFreeDrawPoints = ({ tracePathPointsArr }: CalculatePathFromFreeDrawPointsProps) => {
  const result = touchableAnchorPointPathsForBezierPaths(bezierPathsForTouchPoints(tracePathPointsArr))
    .flat()
    .map<MidPoint>(({ x, y }) => ({ x, y, pointType: PointType.CURVE }));
  return result;
};

function findPerpendicularDistance(p: Point, p1: Point, p2: Point): number {
  let result;
  let slope;
  let intercept;

  // if the line is vertical
  if (p1.x === p2.x) {
    result = Math.abs(p.x - p1.x);
  } else {
    slope = (p2.y - p1.y) / (p2.x - p1.x);
    intercept = p1.y - slope * p1.x;
    result = Math.abs(slope * p.x - p.y + intercept) / Math.sqrt(Math.pow(slope, 2) + 1);
  }

  return result;
}

export interface CalculatePathFromFreeDrawPointsHockeyProps {
  tracePathPointsArr: MidPoint[];
  epsilon: number;
}

// uses Ramer-Douglas-Peucker algorithm
export const calculatePathFromFreeDrawPointsHockey = ({
  tracePathPointsArr,
  epsilon,
}: CalculatePathFromFreeDrawPointsHockeyProps): MidPoint[] => {
  let dmax = 0;
  let index = 0;
  let distance;
  const firstTracePoint = tracePathPointsArr[0] as MidPoint;
  const lastTracePoint = tracePathPointsArr[tracePathPointsArr.length - 1] as MidPoint;

  for (let i = 1; i < tracePathPointsArr.length - 1; i++) {
    const currentPoint = tracePathPointsArr[i] as MidPoint;
    const firstPoint = tracePathPointsArr[0] as MidPoint;
    const lastPoint = tracePathPointsArr[tracePathPointsArr.length - 1] as MidPoint;
    distance = findPerpendicularDistance(currentPoint, firstPoint, lastPoint);
    if (distance > dmax) {
      index = i;
      dmax = distance;
    }
  }

  if (dmax >= epsilon) {
    const recResults1: MidPoint[] = calculatePathFromFreeDrawPointsHockey({
      tracePathPointsArr: tracePathPointsArr.slice(0, index + 1),
      epsilon,
    });
    const recResults2: MidPoint[] = calculatePathFromFreeDrawPointsHockey({
      tracePathPointsArr: tracePathPointsArr.slice(index),
      epsilon,
    });

    const result = recResults1.slice(0, recResults1.length - 1).concat(recResults2);
    return result;
  } else {
    return [firstTracePoint, lastTracePoint];
  }
};
